// 치과 의뢰서 상세(ViewOrder) 상단 「장착 후 확인」 카드 (2026-10-02 대표 승인, 플래그 patientFeedback strict — OFF면 CF 호출 0)
// 기획 docs/기획서_환자피드백_2026-10-01.md v2 §4-1 · 서버 계약 docs/계약_장착후확인_서버_2026-10-02.md §B-6·B-7
//  - 시점 4개 점(빨강=확인 필요 / 초록=회신 / 노랑=예정·회신 대기 / 회색=확인함·종료·삭제) → 누르면 항목별 문항·답·부위·자유 서술
//  - 고정 고지(서버 notice) 항상 표시. 입력 칸 0 · 메모로 복사 0 · 인쇄·복사·내보내기 버튼 0 (법무 L2). 카드는 no-print
//  - [확인함] = acknowledgeFeedback → 확인 후 7일(늦어도 회신 후 30일) 뒤 전달본 삭제
//  - 콘솔에 답 내용·자유 서술·환자명 기록 금지 — 오류는 코드만(clinicFeedbackApi)
import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, RefreshCw } from 'lucide-react';
import { getFeatures, isPatientFeedbackOn } from '../../utils/features';
import {
    POINTS, CLINIC_CARD_NOTICE, STATE_COLOR, getOrderFeedbacks, acknowledgeFeedback, describeFeedbackError,
    pointLabel, pointState, itemLabel, answerLabel, fmtMs, resultLabel,
} from '../../services/clinicFeedbackApi';

const ANSWER_COLOR = { 0: '#475569', 1: '#b45309', 2: '#b91c1c' };

function OrderFeedbackCard({ order, user }) {
    const [on, setOn] = useState(false);
    const [data, setData] = useState(null);       // getOrderFeedbacks 응답
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [openPoint, setOpenPoint] = useState(null);
    const [busy, setBusy] = useState('');
    const [ackMsg, setAckMsg] = useState('');

    const orderId = order?.id;
    const linked = !!order?.patientRecordId;      // 환자 명부에 연결된 의뢰서만 — 미연결이면 호출하지 않는다

    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setOn(isPatientFeedbackOn(f, user, user?.email)); })
            .catch((e) => { console.error('[장착 후 확인] 플래그 조회 실패:', e?.message || e); if (alive) setOn(false); });
        return () => { alive = false; };
    }, [user]);

    const load = useCallback(async () => {
        if (!orderId) return;
        setLoading(true); setError('');
        try {
            const r = await getOrderFeedbacks(orderId);
            setData(r);
            // 처음 열 때: 확인 필요 시점이 있으면 그것을 펼친다
            setOpenPoint(prev => prev || ((r?.points || []).find(p => pointState(p).key === 'need_check')?.point) || null);
        } catch (e) {
            setError(describeFeedbackError(e));
        } finally { setLoading(false); }
    }, [orderId]);

    useEffect(() => { if (on && linked) load(); }, [on, linked, load]);

    const ack = async (p) => {
        if (busy) return;
        setBusy(p.fbId); setAckMsg(''); setError('');
        try {
            const r = await acknowledgeFeedback(p.fbId);
            setAckMsg(`${pointLabel(p.point)} 회신을 확인함으로 표시했습니다.${r?.deleteAt ? ` 전달본은 ${fmtMs(r.deleteAt)}에 삭제됩니다.` : ''}`);
            await load();
        } catch (e) {
            setError(describeFeedbackError(e));
        } finally { setBusy(''); }
    };

    if (!on || !linked) return null;
    const points = Array.isArray(data?.points) ? data.points : [];
    // 연결됐지만 아직 만들어진 시점이 없으면(도착 전·치과 설정 꺼짐 등) 카드를 그리지 않는다. 오류는 보여준다
    if (!error && (loading && !data)) return null;
    if (!error && data && (!data.linked || points.length === 0)) return null;

    const byPoint = Object.fromEntries(points.map(p => [p.point, p]));
    const needCount = points.filter(p => pointState(p).key === 'need_check').length;
    const cur = openPoint ? byPoint[openPoint] : null;

    return (
        <div className="no-print" style={S.card}>
            <div style={S.head}>
                <ClipboardCheck size={18} color="#4f46e5" />
                <span style={S.title}>장착 후 확인</span>
                {needCount > 0 && <span style={S.redBadge}>{`확인 필요 ${needCount}`}</span>}
                <button type="button" onClick={load} disabled={loading} style={{ ...S.chip, marginLeft: 'auto' }}><RefreshCw size={12} /> 새로고침</button>
            </div>

            {/* 시점 4개 점 — 만들어지지 않은 시점(설정 꺼짐·생략)은 빈 점 */}
            <div style={S.dots}>
                {POINTS.map(pt => {
                    const p = byPoint[pt];
                    const st = p ? pointState(p) : null;
                    const c = st ? STATE_COLOR[st.color] : null;
                    const active = openPoint === pt;
                    return (
                        <button key={pt} type="button" disabled={!p} onClick={() => setOpenPoint(active ? null : pt)}
                            style={{ ...S.dotBtn, borderColor: active ? '#4f46e5' : '#e2e8f0', background: active ? '#eef2ff' : '#fff', cursor: p ? 'pointer' : 'default', opacity: p ? 1 : 0.55 }}>
                            <span style={{ ...S.dot, background: c ? c.dot : '#fff', border: c ? 'none' : '1.5px solid #cbd5e1' }} />
                            <span style={{ fontWeight: 700, color: '#0f172a' }}>{pointLabel(pt)}</span>
                            <span style={{ color: c ? c.fg : '#94a3b8' }}>{st ? st.label : '없음'}</span>
                        </button>
                    );
                })}
            </div>

            {cur && <PointDetail p={cur} busy={busy === cur.fbId} onAck={() => ack(cur)} />}

            {ackMsg && <div style={S.ok}>{ackMsg}</div>}
            {error && <div style={S.err}>{error}</div>}
            {/* 고정 고지 — 문구 변경 금지(법무 L2, 서버 CLINIC_CARD_NOTICE) */}
            <div style={S.notice}>{data?.notice || CLINIC_CARD_NOTICE}</div>
        </div>
    );
}

// 시점 1개 상세 — 항목별 문항·답·부위. 답이 없는 상태면 발송·종료 정보만
function PointDetail({ p, busy, onAck }) {
    const st = pointState(p);
    const has = p.status === 'answered' || p.status === 'acknowledged';
    // "아직 장착 전" 답 = status expired · expiredReason not_fitted · fitted false (answered 가 아니라 has 로는 잡히지 않는다)
    const notFitted = p.fitted === false && (p.status === 'expired' || has);
    const items = Array.isArray(p.items) ? p.items : [];
    const questions = Array.isArray(p.questions) ? p.questions : [];
    const sites = Array.isArray(p.sites) ? p.sites : [];

    const meta = [];
    if (p.scheduledAt) meta.push(`예정 ${fmtMs(p.scheduledAt)}`);
    if (p.sentAt) meta.push(`보냄 ${fmtMs(p.sentAt, true)}`);
    if (p.answeredAt) meta.push(`회신 ${fmtMs(p.answeredAt, true)}`);
    if (p.ackAt) meta.push(`확인 ${fmtMs(p.ackAt, true)}`);
    if (p.deleteAt && has) meta.push(`삭제 예정 ${fmtMs(p.deleteAt)}`);
    if (p.lastResult && p.lastResult !== 'ok' && !has) meta.push(`마지막 시도: ${resultLabel(p.lastResult)}`);

    // 항목별 문항 — 부위 선택이 있는 문항은 해당 항목에서만 답을 보여준다(다른 항목은 "다른 부위")
    const siteQs = questions.filter(q => q.siteSelectable);
    const commonQs = questions.filter(q => !q.siteSelectable);
    const cell = (q, it) => {
        const ans = q.answer;
        if (ans === null || ans === undefined) return { text: '답 없음', color: '#94a3b8' };   // 부분 답 스위치(feedbackPartialSubmit) ON 시 미답 문항 — 서버 계약 B-3 answer:null
        if (ans === 0) return { text: answerLabel(q), color: ANSWER_COLOR[0] };
        const qs = sites.filter(s => s.qid === q.qid);
        if (qs.length === 0) return { text: `${answerLabel(q)} (부위 선택 안 함)`, color: ANSWER_COLOR[ans] || '#0f172a' };
        const mine = qs.find(s => s.itemId === it.itemId);
        if (!mine) return { text: '— (다른 부위)', color: '#94a3b8' };
        const teeth = Array.isArray(mine.teeth) ? mine.teeth : [];
        const partial = teeth.length > 0 && teeth.length < (it.teeth || []).length;
        return { text: `${answerLabel(q)}${partial ? ` · ${teeth.join('·')}번` : ''}`, color: ANSWER_COLOR[ans] || '#0f172a' };
    };

    return (
        <div style={S.detail}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ ...S.badge, background: STATE_COLOR[st.color].bg, color: STATE_COLOR[st.color].fg }}>{`${pointLabel(p.point)} · ${st.label}`}</span>
                {items.map(it => <span key={it.itemId} style={S.itemChip}>{itemLabel(it)}</span>)}
                {p.status === 'answered' && (
                    <button type="button" disabled={busy} onClick={onAck} style={{ ...S.ackBtn, opacity: busy ? 0.6 : 1, marginLeft: 'auto' }}>{busy ? '처리 중...' : '확인함'}</button>
                )}
            </div>
            {meta.length > 0 && <div style={S.meta}>{meta.join(' · ')}</div>}

            {notFitted && <div style={S.meta}>{'장착 전으로 회신 — 환자가 "아직 장착 전"이라고 답했습니다.'}</div>}

            {has && p.fitted !== false && questions.length > 0 && (
                <>
                    {items.map(it => {
                        const qs = siteQs.filter(q => (q.itemIds || []).includes(it.itemId));
                        if (qs.length === 0) return null;
                        return (
                            <div key={it.itemId} style={S.itemBlock}>
                                <div style={S.itemTitle}>{itemLabel(it)}</div>
                                {qs.map(q => {
                                    const c = cell(q, it);
                                    return (
                                        <div key={q.qid} style={S.qRow}>
                                            <span style={S.qLabel}>{q.label}</span>
                                            <span style={{ fontWeight: 700, color: c.color }}>{c.text}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                    {commonQs.length > 0 && (
                        <div style={S.itemBlock}>
                            <div style={S.itemTitle}>전체</div>
                            {commonQs.map(q => (
                                <div key={q.qid} style={S.qRow}>
                                    <span style={S.qLabel}>{q.label}</span>
                                    <span style={{ fontWeight: 700, color: ANSWER_COLOR[q.answer] || '#94a3b8' }}>{answerLabel(q)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {p.freeText && (
                        <div style={S.itemBlock}>
                            <div style={S.itemTitle}>환자가 적은 내용</div>
                            <div style={{ fontSize: 13, color: '#0f172a', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{p.freeText}</div>
                        </div>
                    )}
                </>
            )}
            {p.status === 'deleted' && <div style={S.meta}>전달본이 삭제되어 답 내용은 남아 있지 않습니다.</div>}
        </div>
    );
}

const S = {
    card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', margin: '12px 0', userSelect: 'none' },
    head: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
    title: { fontSize: 15, fontWeight: 700, color: '#0f172a' },
    redBadge: { fontSize: 12, fontWeight: 700, color: '#fff', background: '#dc2626', borderRadius: 999, padding: '2px 9px' },
    chip: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#475569', cursor: 'pointer' },
    dots: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 },
    dotBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12.5, fontFamily: 'inherit' },
    dot: { width: 10, height: 10, borderRadius: '50%', display: 'inline-block', boxSizing: 'border-box' },
    detail: { marginTop: 10, borderTop: '1px solid #f1f5f9', paddingTop: 10 },
    badge: { fontSize: 12, fontWeight: 700, borderRadius: 999, padding: '3px 10px' },
    itemChip: { fontSize: 12, fontWeight: 600, color: '#334155', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 999, padding: '3px 9px' },
    ackBtn: { padding: '6px 14px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
    meta: { fontSize: 12, color: '#64748b', marginTop: 6 },
    itemBlock: { marginTop: 10, background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: 8, padding: '8px 12px' },
    itemTitle: { fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 4 },
    qRow: { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '4px 0', fontSize: 13, borderBottom: '1px dashed #e2e8f0' },
    qLabel: { color: '#334155' },
    ok: { marginTop: 10, background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', borderRadius: 8, padding: '8px 11px', fontSize: 12.5 },
    err: { marginTop: 10, background: '#fdecec', border: '1px solid #f5cfcf', color: '#8a2020', borderRadius: 8, padding: '8px 11px', fontSize: 12.5 },
    notice: { marginTop: 10, fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px', lineHeight: 1.5 },
};

export default OrderFeedbackCard;
